import type { TerminalSocket } from '../transport/ws'
import type { SharedKey } from '../transport/relay'

/**
 * #keysMenu — the keys a phone's soft keyboard doesn't have.
 *
 * Seven keys, with the original's escape sequences:
 *   Tab \t · ⇧Tab \x1b[Z · / · ↑ \x1b[A · ↓ \x1b[B · ← \x1b[D · → \x1b[C
 *
 * WHAT HAS LEFT THIS ROW, and why none of it is a loss:
 *
 * The Ctrl and Alt sticky latches went first, at the owner's request — so Ctrl+D,
 * Ctrl+L, Ctrl+R and friends have no on-screen route.
 *
 * Esc went on 2026-09-13 (owner: 現在可以把虛擬小鍵盤左邊的這個 Escape key 給移除了):
 * the Stop button in the composer's button row (#stopBtn, App.tsx) writes the same
 * \x1b while the agent is working, which is the moment anyone reached for it.
 *
 * ^C and ^Z went the same day (owner: 這個 Ctrl-C 或是 Ctrl-Z 的快捷虛擬按鈕應該可以取消掉了,
 * 它是一個 legacy 的功能). They were here for driving a bare shell by thumb; this app
 * drives agents, and an agent's own interrupt is Esc — the Stop button. A hardware
 * keyboard sends all four of these to the pty directly, as it always has.
 *
 * Every button preventDefaults `mousedown`: tapping a key must never steal focus from
 * the composer, or the soft keyboard hides and re-shows and the layout jumps (§5.1).
 */

/**
 * `name`: the same key as a word, for a friend's agent (owner, 2026-10-17: 小鍵盤 B) — a friend holds
 * no pty, so the key goes through the share by name and the machine maps it back (agentKey).
 */
export const KEYS: { label: string; seq: string; name: SharedKey; title?: string; arrow?: boolean }[] = [
  { label: 'Tab', seq: '\t', name: 'tab' },
  { label: '⇧Tab', seq: '\x1b[Z', name: 'shift_tab', title: 'Shift+Tab' },
  { label: '/', seq: '/', name: 'slash' },
  { label: '↑', seq: '\x1b[A', name: 'up', arrow: true },
  { label: '↓', seq: '\x1b[B', name: 'down', arrow: true },
  { label: '←', seq: '\x1b[D', name: 'left', arrow: true },
  { label: '→', seq: '\x1b[C', name: 'right', arrow: true },
]

/**
 * A bare terminal's own two (＋ → Terminal, 2026-10-18): out of the agents' row on purpose —
 * Esc is an agent's Stop and the Stop button owns it, Ctrl-C went with it — but a plain shell
 * on a phone has no other way to send either.
 */
const SHELL_KEYS: { label: string; seq: string; title: string }[] = [
  { label: 'Esc', seq: '\x1b', title: 'Escape' },
  { label: '^C', seq: '\x03', title: 'Ctrl+C' },
]

export function KeysMenu({
  socketRef,
  onKey,
  locked,
  hideArrows,
  shellKeys,
}: {
  socketRef: { current: TerminalSocket | null }
  /** Instead of the pty: a friend's agent, by the key's name (sharedSend.pressShared). */
  onKey?: (name: SharedKey) => void
  /** Pinned: the row stays open across keyboard toggles (double-tap ⌨). */
  locked?: boolean
  /**
   * Drop ↑↓←→ when the composer's swipe d-pad is providing them (§5.2). Two ways to send
   * the same key is clutter on the surface with the least room — and the arrows are the
   * four widest keys in the row.
   */
  hideArrows?: boolean
  /** A bare terminal: Esc and Ctrl-C lead the row. */
  shellKeys?: boolean
}) {
  const send = (k: (typeof KEYS)[number]) => (onKey ? onKey(k.name) : socketRef.current?.write(k.seq))
  const keys = hideArrows ? KEYS.filter((k) => !k.arrow) : KEYS

  return (
    <div
      id="keysMenu"
      className={`open${locked ? ' locked' : ''}`}
      role="group"
      aria-label="Extra keys"
    >
      {shellKeys &&
        SHELL_KEYS.map((k) => (
          <button
            key={k.label}
            type="button"
            className="kb-key"
            title={k.title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => socketRef.current?.write(k.seq)}
          >
            {k.label}
          </button>
        ))}
      {keys.map((k) => (
        <button
          key={k.label}
          type="button"
          className="kb-key"
          title={k.title ?? k.label}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => send(k)}
        >
          {k.label}
        </button>
      ))}
    </div>
  )
}

